import { MessageType } from '@graphql/generated-base';
import type { InternalMessage } from '../../MessagesV2.types';

/**
 * Only email rows can be resent this round. The backend has an SMS resend and it is tested, but
 * nothing calls it yet — SMS rows render a disabled checkbox so it reads as "not yet" rather than
 * as a missing column.
 */
export const isResendable = (message: InternalMessage): boolean => message.type === MessageType.email;

/**
 * A row is a resend of something else when it carries the original's id. Drives both the badge and
 * the expand-to-original subrow.
 *
 * NOTE `originalMessageId` holds the ORIGINAL ROW's uuid, not a provider message id — contrast
 * `messageId`, which is exactly that.
 */
export const isResendRow = (message: InternalMessage): boolean => Boolean(message.originalMessageId);

/** The resendable subset of what's on screen — what select-all acts on. */
export const resendableOf = (messages: readonly InternalMessage[]): InternalMessage[] => messages.filter(isResendable);

/**
 * Select-all is "on" only when every resendable row is already ticked. Vacuously true on an empty
 * list would render a ticked header over nothing, so an empty list is explicitly not all-selected.
 */
export const areAllResendableSelected = (messages: readonly InternalMessage[], selectedIds: ReadonlySet<string>): boolean => {
  const resendable = resendableOf(messages);
  return resendable.length > 0 && resendable.every(message => selectedIds.has(message.id));
};
